import React, { useState } from "react";
import { BarChart } from "./Bar";
import DataTable from "react-data-table-component";

function InspFranchiseDashboard() {
  const [chartData] = useState({
    cityWise: [
      {
        city: "Lahore",
        region: "Central",
        campuses: 4,
        students: 700,
        revenue: 2800000,
        royalty: 2100000,
      },
      {
        city: "Faisalabad",
        region: "Central",
        campuses: 3,
        students: 500,
        revenue: 2000000,
        royalty: 1500000,
      },
      {
        city: "Multan",
        region: "Central",
        campuses: 2,
        students: 300,
        revenue: 1000000,
        royalty: 700000,
      },
      {
        city: "Islamabad",
        region: "North",
        campuses: 2,
        students: 700,
        revenue: 2600000,
        royalty: 1900000,
      },
      {
        city: "Rawalpindi",
        region: "North",
        campuses: 2,
        students: 850,
        revenue: 3000000,
        royalty: 2200000,
      },
      {
        city: "Peshawar",
        region: "North",
        campuses: 1,
        students: 450,
        revenue: 1200000,
        royalty: 900000,
      },
      {
        city: "Karachi",
        region: "South",
        campuses: 8,
        students: 900,
        revenue: 3600000,
        royalty: 2800000,
      },
      {
        city: "Hyderabad",
        region: "South",
        campuses: 4,
        students: 400,
        revenue: 1500000,
        royalty: 1100000,
      },
      {
        city: "Sukkur",
        region: "South",
        campuses: 3,
        students: 300,
        revenue: 900000,
        royalty: 700000,
      },
    ],
  });

  // Calculate totals from cityWise data
  const centralData = chartData.cityWise
    .filter((item) => item.region === "Central")
    .reduce(
      (acc, item) => ({
        campuses: acc.campuses + item.campuses,
        students: acc.students + item.students,
      }),
      { campuses: 0, students: 0 }
    );

  const northData = chartData.cityWise
    .filter((item) => item.region === "North")
    .reduce(
      (acc, item) => ({
        campuses: acc.campuses + item.campuses,
        students: acc.students + item.students,
      }),
      { campuses: 0, students: 0 }
    );

  const southData = chartData.cityWise
    .filter((item) => item.region === "South")
    .reduce(
      (acc, item) => ({
        campuses: acc.campuses + item.campuses,
        students: acc.students + item.students,
      }),
      { campuses: 0, students: 0 }
    );

  const totalRevenue = chartData.cityWise.reduce(
    (acc, item) => acc + item.revenue,
    0
  );

  const totalRoyalty = chartData.cityWise.reduce(
    (acc, item) => acc + item.royalty,
    0
  );

  const revenueOutstanding = Math.round(totalRevenue * 0.1);
  const royaltyOutstanding = Math.round(totalRoyalty * 0.1);

  // Format numbers
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    return num.toLocaleString();
  };

  // Royalty Fee Receivable Aging Chart Data (Stacked Bar Chart)
  const royaltyAgingData = {
    labels: [
      "Alpha Edu",
      "Bright Campus",
      "City Scholars",
      "Delta Kids",
      "Eagle Academy",
    ],
    datasets: [
      {
        label: "0-30",
        data: [0.6, 0.5, 0.4, 0.3, 0.4],
        backgroundColor: "#00bfff",
        borderColor: "#00bfff",
        borderWidth: 1,
      },
      {
        label: "31-60",
        data: [0.3, 0.2, 0.25, 0.15, 0.15],
        backgroundColor: "#ff69b4",
        borderColor: "#ff69b4",
        borderWidth: 1,
      },
      {
        label: "61-90",
        data: [0.1, 0.15, 0.2, 0.1, 0.1],
        backgroundColor: "#ffa500",
        borderColor: "#ffa500",
        borderWidth: 1,
      },
      {
        label: ">90",
        data: [0.1, 0.1, 0.15, 0.2, 0.2],
        backgroundColor: "#ffff00",
        borderColor: "#ffff00",
        borderWidth: 1,
      },
    ],
  };

  // Help Desk Tickets Pie Chart Data
  const helpDeskData = {
    labels: ["Open", "In Progress", "SLA Breached", "Closed"],
    datasets: [
      {
        data: [35, 10, 10, 45],
        backgroundColor: [
          "#00bfff", // Open - Blue
          "#ffff00", // In Progress - Yellow
          "#ff69b4", // SLA Breached - Pink
          "#ffa500", // Closed - Orange
        ],
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  // Month-wise Revenue vs Expenses Line Chart Data
  const monthlyData = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Net (Revenue - Expenses)",
        data: [0.1, 0.05, 0.15, 0.4, 0.5, 0.55, 0.4, 0.2, 0.2, 0.3, 0.6, 0.8],
        borderColor: "#00bfff",
        backgroundColor: "rgba(0, 191, 255, 0.1)",
        tension: 0.4,
        fill: true,
        pointBackgroundColor: "#00bfff",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  // City-wise Table Columns
  const cityColumns = [
    {
      name: "City",
      selector: (row) => row.city,
      sortable: true,
      width: "120px",
    },
    {
      name: "Region",
      selector: (row) => row.region,
      sortable: true,
      width: "100px",
    },
    {
      name: "Campuses",
      selector: (row) => row.campuses,
      sortable: true,
      width: "100px",
    },
    {
      name: "Students",
      selector: (row) => row.students,
      sortable: true,
      width: "100px",
    },
    {
      name: "Franchise Revenue",
      selector: (row) => `Rs. ${row.revenue.toLocaleString()}`,
      sortable: true,
      width: "150px",
    },
    {
      name: "Royalty Fee",
      selector: (row) => `Rs. ${row.royalty.toLocaleString()}`,
      sortable: true,
      width: "150px",
    },
  ];

  // Custom table styles
  const customTableStyles = {
    headCells: {
      style: {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
        fontSize: "14px",
        color: "#2c3e50",
      },
    },
    cells: {
      style: {
        fontSize: "13px",
        padding: "8px",
      },
    },
  };

  return (
    <>
      {/* Header */}
      <div
        className="mb-4"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h3 className="mb-2" style={{ color: "#2c3e50", fontWeight: "700" }}>
            IISS Franchise Dashboard
          </h3>
          <p className="text-body-tertiary" style={{ fontSize: "16px" }}>
            Comprehensive overview of franchise performance and metrics
          </p>
        </div>
      </div>

      {/* Enhanced Top KPI Tiles */}
      <div className="row g-3 mb-4">
        {/* Central Card */}
        <div className="col-6 col-md">
          <div
            className="card rounded-4 p-3 h-100 position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              border: "none",
              boxShadow: "0 4px 12px rgba(52, 152, 219, 0.15)",
              minHeight: "120px",
            }}
          >
            <div
              className="position-absolute top-0 start-0 w-100"
              style={{ height: "4px", backgroundColor: "#3498db" }}
            ></div>
            <div className="d-flex align-items-center h-100">
              <div
                className="me-3 d-flex align-items-center justify-content-center rounded-3"
                style={{
                  color: "#3498db",
                  fontSize: "20px",
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(52, 152, 219, 0.1)",
                }}
              >
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div className="flex-grow-1">
                <div
                  className="text-uppercase fw-bold mb-1"
                  style={{
                    fontSize: "10px",
                    color: "#7f8c8d",
                    letterSpacing: "0.5px",
                  }}
                >
                  Central
                </div>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-baseline mb-1">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#2c3e50",
                        fontSize: "18px",
                        lineHeight: "1",
                      }}
                    >
                      {centralData.campuses}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Campuses
                    </span>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#2c3e50",
                        fontSize: "18px",
                        lineHeight: "1",
                      }}
                    >
                      {centralData.students.toLocaleString()}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Students
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* North Card */}
        <div className="col-6 col-md">
          <div
            className="card rounded-4 p-3 h-100 position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              border: "none",
              boxShadow: "0 4px 12px rgba(46, 204, 113, 0.15)",
              minHeight: "120px",
            }}
          >
            <div
              className="position-absolute top-0 start-0 w-100"
              style={{ height: "4px", backgroundColor: "#2ecc71" }}
            ></div>
            <div className="d-flex align-items-center h-100">
              <div
                className="me-3 d-flex align-items-center justify-content-center rounded-3"
                style={{
                  color: "#2ecc71",
                  fontSize: "20px",
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(46, 204, 113, 0.1)",
                }}
              >
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div className="flex-grow-1">
                <div
                  className="text-uppercase fw-bold mb-1"
                  style={{
                    fontSize: "10px",
                    color: "#7f8c8d",
                    letterSpacing: "0.5px",
                  }}
                >
                  North
                </div>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-baseline mb-1">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#2c3e50",
                        fontSize: "18px",
                        lineHeight: "1",
                      }}
                    >
                      {northData.campuses}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Campuses
                    </span>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#2c3e50",
                        fontSize: "18px",
                        lineHeight: "1",
                      }}
                    >
                      {northData.students.toLocaleString()}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Students
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* South Card */}
        <div className="col-6 col-md">
          <div
            className="card rounded-4 p-3 h-100 position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              border: "none",
              boxShadow: "0 4px 12px rgba(155, 89, 182, 0.15)",
              minHeight: "120px",
            }}
          >
            <div
              className="position-absolute top-0 start-0 w-100"
              style={{ height: "4px", backgroundColor: "#9b59b6" }}
            ></div>
            <div className="d-flex align-items-center h-100">
              <div
                className="me-3 d-flex align-items-center justify-content-center rounded-3"
                style={{
                  color: "#9b59b6",
                  fontSize: "20px",
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(155, 89, 182, 0.1)",
                }}
              >
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div className="flex-grow-1">
                <div
                  className="text-uppercase fw-bold mb-1"
                  style={{
                    fontSize: "10px",
                    color: "#7f8c8d",
                    letterSpacing: "0.5px",
                  }}
                >
                  South
                </div>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-baseline mb-1">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#2c3e50",
                        fontSize: "18px",
                        lineHeight: "1",
                      }}
                    >
                      {southData.campuses}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Campuses
                    </span>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#2c3e50",
                        fontSize: "18px",
                        lineHeight: "1",
                      }}
                    >
                      {southData.students.toLocaleString()}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Students
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Franchise Revenue Card */}
        <div className="col-6 col-md">
          <div
            className="card rounded-4 p-3 h-100 position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              border: "none",
              boxShadow: "0 4px 12px rgba(230, 126, 34, 0.15)",
              minHeight: "120px",
            }}
          >
            <div
              className="position-absolute top-0 start-0 w-100"
              style={{ height: "4px", backgroundColor: "#e67e22" }}
            ></div>
            <div className="d-flex align-items-center h-100">
              <div
                className="me-3 d-flex align-items-center justify-content-center rounded-3"
                style={{
                  color: "#e67e22",
                  fontSize: "20px",
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(230, 126, 34, 0.1)",
                }}
              >
                <i className="fas fa-hand-holding-usd"></i>
              </div>
              <div className="flex-grow-1">
                <div
                  className="text-uppercase fw-bold mb-1"
                  style={{
                    fontSize: "10px",
                    color: "#7f8c8d",
                    letterSpacing: "0.5px",
                  }}
                >
                  Franchise Revenue
                </div>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-baseline mb-1">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#2c3e50",
                        fontSize: "16px",
                        lineHeight: "1",
                      }}
                    >
                      Rs. {formatNumber(totalRevenue)}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Total
                    </span>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#e74c3c",
                        fontSize: "16px",
                        lineHeight: "1",
                      }}
                    >
                      Rs. {formatNumber(revenueOutstanding)}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Outstanding
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Royalty Fee Card */}
        <div className="col-6 col-md">
          <div
            className="card rounded-4 p-3 h-100 position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              border: "none",
              boxShadow: "0 4px 12px rgba(52, 73, 94, 0.15)",
              minHeight: "120px",
            }}
          >
            <div
              className="position-absolute top-0 start-0 w-100"
              style={{ height: "4px", backgroundColor: "#34495e" }}
            ></div>
            <div className="d-flex align-items-center h-100">
              <div
                className="me-3 d-flex align-items-center justify-content-center rounded-3"
                style={{
                  color: "#34495e",
                  fontSize: "20px",
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(52, 73, 94, 0.1)",
                }}
              >
                <i className="fas fa-file-invoice-dollar"></i>
              </div>
              <div className="flex-grow-1">
                <div
                  className="text-uppercase fw-bold mb-1"
                  style={{
                    fontSize: "10px",
                    color: "#7f8c8d",
                    letterSpacing: "0.5px",
                  }}
                >
                  Royalty Fee
                </div>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-baseline mb-1">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#2c3e50",
                        fontSize: "16px",
                        lineHeight: "1",
                      }}
                    >
                      Rs. {formatNumber(totalRoyalty)}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Total
                    </span>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <span
                      className="fw-bold me-2"
                      style={{
                        color: "#e74c3c",
                        fontSize: "16px",
                        lineHeight: "1",
                      }}
                    >
                      Rs. {formatNumber(royaltyOutstanding)}
                    </span>
                    <span style={{ fontSize: "11px", color: "#7f8c8d" }}>
                      Outstanding
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section - Remaining the same */}
      <div className="row g-4">
        {/* City-wise Table */}
        <div className="col-lg-6">
          <div className="card mb-3 shadow-sm h-100">
            <div className="card-body">
              <h6
                className="card-title fw-bold"
                style={{ color: "#2c3e50", marginBottom: "20px" }}
              >
                <i
                  className="fas fa-city me-2"
                  style={{ color: "#3498db" }}
                ></i>
                City-wise Campuses, Franchise Revenue, Royalty Fee (YTD)
              </h6>
              <DataTable
                columns={cityColumns}
                data={chartData.cityWise}
                customStyles={customTableStyles}
                pagination
                responsive
                dense
              />
            </div>
          </div>
        </div>

        {/* Month-wise Revenue vs Expenses Line Chart */}
        <div className="col-lg-6">
          <div className="card mb-3 shadow-sm h-100">
            <div className="card-body">
              <h6
                className="card-title fw-bold"
                style={{ color: "#2c3e50", marginBottom: "20px" }}
              >
                <i
                  className="fas fa-chart-line me-2"
                  style={{ color: "#2ecc71" }}
                ></i>
                Month-wise Revenue vs Expenses
              </h6>
              <BarChart
                chartType="monthlyLine"
                chartData={monthlyData}
                height="300px"
              />
            </div>
          </div>
        </div>

        {/* Royalty Fee Receivable Aging Stacked Bar Chart */}
        <div className="col-lg-6">
          <div className="card mb-3 shadow-sm h-100">
            <div className="card-body">
              <h6
                className="card-title fw-bold"
                style={{ color: "#2c3e50", marginBottom: "20px" }}
              >
                <i
                  className="fas fa-clock me-2"
                  style={{ color: "#9b59b6" }}
                ></i>
                Royalty Fee Receivable Aging
              </h6>
              <BarChart
                chartType="royaltyAging"
                chartData={royaltyAgingData}
                height="300px"
              />
            </div>
          </div>
        </div>

        {/* Help Desk Tickets Status Pie Chart */}
        <div className="col-lg-6">
          <div className="card mb-3 shadow-sm h-100">
            <div className="card-body">
              <h6
                className="card-title fw-bold"
                style={{ color: "#2c3e50", marginBottom: "20px" }}
              >
                <i
                  className="fas fa-ticket-alt me-2"
                  style={{ color: "#e67e22" }}
                ></i>
                Help Desk Tickets Status
              </h6>
              <div className="text-center mb-2">
                <small className="text-muted">
                  SLA target: resolve within 48 hours
                </small>
              </div>
              <BarChart
                chartType="helpDeskPie"
                chartData={helpDeskData}
                height="250px"
              />
              <div
                className="text-center mt-3"
                style={{ color: "#00ff00", fontWeight: "bold" }}
              >
                SLA Compliance: 96%
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default InspFranchiseDashboard;
